import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Shield, Lock, Database, Users, FileText, AlertTriangle } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    // If opened in new tab/window, close it; otherwise navigate back
    if (window.history.length <= 1 || window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={handleBack} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Link to="/" className="inline-flex items-center rounded-sm text-sm font-medium text-blue-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600">
            ← Back to Home
          </Link>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 text-blue-600" />
              <CardTitle className="text-3xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Privacy Policy
              </CardTitle>
            </div>
            <p className="text-sm text-gray-600">
              Last Updated: January 30, 2025
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to INSIGHT. We are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our online counseling platform.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Information We Collect
              </h2>
              <div className="space-y-3 text-gray-700">
                <div>
                  <h3 className="font-semibold mb-2">Personal Information:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Name, email address, and contact information</li>
                    <li>Registration details (for psychologists: license/registration number)</li>
                    <li>Profile information (qualifications, specialization, experience)</li>
                    <li>Profile photos (for psychologists)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Session Data:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Analysis responses (5-phase structured qualitative interpretative summary)</li>
                    <li>Consultation requests and appointment information</li>
                    <li>Questions and answers between clients and psychologists</li>
                    <li>Feedback and ratings</li>
                    <li>Generated Analysis</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Video Consultation Metadata:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>What we store:</strong> Consultation ID, participant IDs (NOT names), timestamps, call duration, consent records</li>
                    <li><strong>What we DO NOT store:</strong> Video recordings, audio recordings, call content, transcripts, or chat messages</li>
                    <li><strong>Privacy protection:</strong> Participant names are NOT stored in video call records (anonymous mode)</li>
                    <li><strong>Purpose:</strong> Consent verification, billing, quality assurance, compliance with Telemedicine Practice Guidelines 2020</li>
                    <li><strong>Retention:</strong> Automatically deleted after 5 years (you can request earlier deletion)</li>
                    <li><strong>Encryption:</strong> End-to-end encrypted video calls via Jitsi Meet - we cannot access call content</li>
                  </ul>
                  <p className="text-sm text-blue-600 mt-2 italic">
                    Note: Video call metadata is required for legal compliance (DPDPA 2023 and Indian Telemedicine Practice Guidelines 2020). 
                    We store the MINIMUM data necessary and protect your privacy through anonymous storage and automatic deletion.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Voice Input (Speech-to-Text):</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Your voice is processed on your device to create text. No audio is recorded or stored.</strong></li>
                    <li>Speech recognition uses your browser&apos;s built-in Web Speech API — audio never leaves your device</li>
                    <li>Only the transcribed text of your responses is saved as your session data (with the same protections as typed answers)</li>
                    <li>You can switch to typing at any point and voice mode is fully optional</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Technical Information:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>IP address and browser information</li>
                    <li>Session timestamps and activity logs</li>
                    <li>Device information</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                How We Use Your Information
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>To provide and maintain our counseling platform</li>
                <li>To match clients with appropriate psychologists</li>
                <li>To facilitate communication between clients and psychologists</li>
                <li>To generate structured qualitative interpretative summary and Analysis</li>
                <li>To improve our services and user experience</li>
                <li>To send notifications about appointments and platform updates</li>
                <li>To comply with legal obligations</li>
                <li>To detect and prevent fraud or abuse</li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-600" />
                Data Security
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Password encryption using industry-standard bcrypt hashing</li>
                <li>Secure authentication using JWT tokens</li>
                <li>HTTPS encryption for all data transmission</li>
                <li>Role-based access control</li>
                <li>Regular security audits and updates</li>
                <li>Secure database storage</li>
              </ul>
            </section>

            {/* Your Rights (GDPR/DPDPA) */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Your Rights
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Under data protection laws (GDPR/DPDPA), you have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Right to Access:</strong> You can download all your personal data at any time from your account settings</li>
                <li><strong>Right to Rectification:</strong> You can update your profile information through your account settings</li>
                <li><strong>Right to Erasure:</strong> You can delete your account and request anonymization of your data</li>
                <li><strong>Right to Data Portability:</strong> You can export your data in JSON format</li>
                <li><strong>Right to Object:</strong> You can object to certain data processing activities</li>
                <li><strong>Right to Withdraw Consent:</strong> You can withdraw your consent at any time</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Data Retention</h2>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>
                  We retain your personal data for as long as your account is active or as needed to provide you services. 
                  When you delete your account, we anonymize your personal information while retaining anonymized data for 
                  analytics and legal compliance purposes. Analysis data is deleted immediately upon account deletion.
                </p>
                <div>
                  <h3 className="font-semibold mb-2">Specific Retention Periods:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>User Profile Data:</strong> Retained while account is active; anonymized upon deletion</li>
                    <li><strong>Session & Analysis Data:</strong> Retained for 3 years or until account deletion</li>
                    <li><strong>Video Call Metadata:</strong> Automatically deleted after 5 years (or earlier upon request)</li>
                    <li><strong>Consent Records:</strong> Retained for 5 years for legal compliance</li>
                    <li><strong>Audit Logs:</strong> Retained for 3 years for security and compliance</li>
                  </ul>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Your Control:</strong> You can request deletion of your video call history at any time from your dashboard. 
                    The system also automatically deletes video call records older than 5 years as part of our data minimization policy.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Data Sharing</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We do not sell your personal information. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>With Psychologists:</strong> Your analysis data is shared with your chosen psychologist</li>
                <li><strong>With Service Providers:</strong> We use third-party services (OpenAI, Google Gemini) for AI-powered features</li>
                <li><strong>For Legal Requirements:</strong> If required by law or to protect our rights</li>
                <li><strong>With Your Consent:</strong> Any other sharing will be done only with your explicit consent</li>
              </ul>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the following third-party services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>OpenAI:</strong> For AI-powered Analysis generation</li>
                <li><strong>Google Gemini:</strong> For AI-powered features</li>
                <li><strong>MongoDB:</strong> For secure database storage</li>
                <li><strong>Emergent Platform:</strong> For hosting and infrastructure</li>
              </ul>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to maintain your session and improve user experience. 
                We use JWT tokens stored in sessionStorage for authentication. You can control cookies through your browser settings.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Children's Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our service is not intended for individuals under the age of 18. We do not knowingly collect personal 
                information from children. If you are a parent or guardian and believe your child has provided us with 
                personal information, please contact us.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
                new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-blue-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold mb-3">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> privacy@insight-platform.com<br />
                <strong>Address:</strong> INSIGHT Platform, India
              </p>
            </section>

            {/* DPDPA Specific */}
            <section className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-3">DPDPA (India) Compliance</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                This platform complies with the Digital Personal Data Protection Act, 2023 (DPDPA) of India. 
                Your data is processed lawfully and with your consent. You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Access your personal data</li>
                <li>Correct inaccurate personal data</li>
                <li>Request erasure of personal data</li>
                <li>Nominate another person to exercise your rights</li>
                <li>File a complaint with the Data Protection Board of India</li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
